import {
  COMPANY_DRAWERS,
  COMPANY_PROFILE_CONFIG,
  COMPANY_TAB_IDS
} from 'features/inventory-profiles/constants';
import { buildTabDataTableProps, getCommonTabs } from 'features/inventory-profiles/utils';
import strings from 'strings';
import { SalesTab } from 'UI/components/organisms/inventoryProfiles';
import AssociatedCompaniesTab from 'UI/components/organisms/inventoryProfiles/AssociatedCompaniesTab';
import CompanyOverview from 'UI/components/organisms/inventoryProfiles/CompanyOverview';
import EmployeesTab from 'UI/components/organisms/inventoryProfiles/EmployeesTab';
import FeeAgreementTab from 'UI/components/organisms/inventoryProfiles/FeeAgreementTab';
import HiringAuthorityTab from 'UI/components/organisms/inventoryProfiles/HiringAuthorityTab';
import JobOrdersTab from 'UI/components/organisms/inventoryProfiles/JobOrdersTab';
import PlacementsTab from 'UI/components/organisms/inventoryProfiles/PlacementsTab';
import SendoutsTab from 'UI/components/organisms/inventoryProfiles/SendoutsTab';
import { TasksTab } from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { EntityType } from 'UI/constants/entityTypes';

import { COMPANY_TABS_CONFIG } from './CompanyProfileV2.constants';

const COPIES = strings.inventoryProfiles;

/**
 * Tabs of the Company profile. Each tab gets only the props its V1 component reads, written one
 * by one with no spread. The Sendouts tab has no "new" button: SendoutsTab only uses
 * `onNewItemClick` in Job Order mode.
 * @param {Object} params
 * @param {Object} params.company Company from useCompanyProfile
 * @param {string} params.companyName `formattedCompany.header.title` (V1 `itemName`)
 * @param {boolean} params.isLoading The profile is loading
 * @param {Object} params.permissions Result of useCompanyPermissions
 * @param {(name: string) => (item: { id: number|string }) => void} params.openItemDrawer From useProfileDrawer
 * @param {() => void} params.refresh Reloads the profile silently
 * @param {(tabId: string) => void} params.refreshTab From useTabRefresh: remounts Placements after a
 *   save, which also resets PlacementsTab's one-shot refresh flag
 * @param {{ id: string, tabProps: Object }} params.searchProjectsTab `searchProjectTabPropsDefinition` of useSearchProjectInProfiles (V1)
 * @returns {Array<{ id: string, label: string, content: React.ReactNode, visible: boolean }>}
 */
export const buildCompanyTabs = ({
  company,
  companyName,
  isLoading,
  openItemDrawer,
  permissions,
  refresh,
  refreshTab,
  searchProjectsTab
}) => {
  const profileEndpoint = `${COMPANY_PROFILE_CONFIG.endpoint}/${company.id}`;
  const {
    [COMPANY_TAB_IDS.JobOrders]: jobOrders,
    [COMPANY_TAB_IDS.Sendouts]: sendouts,
    [COMPANY_TAB_IDS.Placements]: placements,
    [COMPANY_TAB_IDS.FeeAgreements]: feeAgreements,
    [COMPANY_TAB_IDS.HiringAuthorities]: hiringAuthorities,
    [COMPANY_TAB_IDS.AssociatedCompanies]: associatedCompanies,
    [COMPANY_TAB_IDS.Employees]: employees,
    [COMPANY_TAB_IDS.ActionPlan]: actionPlan,
    [COMPANY_TAB_IDS.Sales]: sales
  } = COMPANY_TABS_CONFIG;
  const profileTableProps = tabConfig => buildTabDataTableProps({ profileEndpoint, tabConfig });

  return [
    {
      id: COMPANY_TAB_IDS.Overview,
      label: COPIES.common.tabs.overview,
      visible: true,
      content: <CompanyOverview loading={isLoading} profile={company} />
    },
    {
      id: COMPANY_TAB_IDS.JobOrders,
      label: COPIES.common.tabs.jobOrders,
      visible: true,
      content: (
        <JobOrdersTab
          DataTableProps={profileTableProps(jobOrders)}
          initialColumns={jobOrders.initialColumns}
          itemName={companyName}
          mode={EntityType.Company}
          profileId={company.id}
          tablePreferencesKey={jobOrders.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.Sendouts,
      label: COPIES.common.tabs.sendouts,
      visible: true,
      content: (
        <SendoutsTab
          DataTableProps={profileTableProps(sendouts)}
          extraRenderers={sendouts.extraRenderers}
          initialColumns={sendouts.initialColumns}
          onSendoutClick={openItemDrawer(COMPANY_DRAWERS.Sendout)}
          profile={company}
          profileModule={EntityType.Company}
          tablePreferencesKey={sendouts.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.Placements,
      label: COPIES.common.tabs.placements,
      visible: true,
      content: (
        <PlacementsTab
          DataTableProps={profileTableProps(placements)}
          initialColumns={placements.initialColumns}
          isLoading={isLoading}
          onTriggerTabRefresh={refreshTab}
          profileId={company.id}
          profileModule={EntityType.Company}
          tablePreferencesKey={placements.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.FeeAgreements,
      label: COPIES.common.tabs.feeAgreements,
      visible: true,
      content: (
        <FeeAgreementTab
          DataTableProps={profileTableProps(feeAgreements)}
          endpoint={profileEndpoint}
          initialColumns={feeAgreements.initialColumns}
          profile={company}
          profileModule={EntityType.Company}
          search={false}
          tablePreferencesKey={feeAgreements.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.HiringAuthorities,
      label: COPIES.common.tabs.hiringAuthorities,
      visible: true,
      content: (
        <HiringAuthorityTab
          DataTableProps={profileTableProps(hiringAuthorities)}
          initialColumns={hiringAuthorities.initialColumns}
          isCompanyProfile
          profile={company}
          tablePreferencesKey={hiringAuthorities.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.AssociatedCompanies,
      label: COPIES.common.tabs.associatedCompanies,
      visible: permissions.canSeeAssociatedCompanies,
      content: (
        <AssociatedCompaniesTab
          DataTableProps={profileTableProps(associatedCompanies)}
          itemName={companyName}
          profileId={company.id}
          tablePreferencesKey={associatedCompanies.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.Employees,
      label: COPIES.common.tabs.employees,
      visible: true,
      content: (
        <EmployeesTab
          DataTableProps={profileTableProps(employees)}
          profile={company}
          tablePreferencesKey={employees.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.ActionPlan,
      label: COPIES.sections.tabs.actionPlan.tabLabel,
      visible: permissions.canUseDailyPlan,
      content: (
        <TasksTab
          allowedTaskTypes={actionPlan.allowedTaskTypes}
          DataTableProps={actionPlan.DataTableProps}
          entityType={actionPlan.entityType}
          entityTypeId={actionPlan.entityTypeId}
          extraRenderers={actionPlan.extraRenderers}
          initialColumns={actionPlan.initialColumns}
          itemName={companyName}
          profileId={company.id}
          tablePreferencesKey={actionPlan.tablePreferencesKey}
        />
      )
    },
    {
      id: COMPANY_TAB_IDS.Sales,
      label: COPIES.common.tabs.sales,
      visible: permissions.canSeeSalesTab,
      content: (
        <SalesTab
          DataTableProps={sales.DataTableProps}
          initialColumns={sales.initialColumns}
          profileId={company.id}
          tablePreferencesKey={sales.tablePreferencesKey}
        />
      )
    },
    ...getCommonTabs({
      profileId: company.id,
      entity: COMPANY_PROFILE_CONFIG,
      itemName: companyName,
      onActivityComplete: refresh,
      searchProjectsTab
    })
  ];
};
